import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, CheckCircle, AlertTriangle, Battery, Thermometer } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const PrevenirBateriaDescargada = () => {
  const navigate = useNavigate();

  // SEO: Set canonical to avoid duplicate content issues
  useSEO({
    title: 'Cómo Prevenir que la Batería se Descargue | Consejos ArrancaYa BCN',
    description: 'Tips profesionales para prevenir que tu batería de coche se descargue. Mantenimiento preventivo que te ahorrará disgustos y dinero en Barcelona.',
    canonical: 'https://arrancaya-bcn.es/blog/prevenir-bateria-descargada'
  });

  useEffect(() => {

    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'prevenir batería descargada, mantenimiento batería coche, cuidado batería, evitar descarga Barcelona';
    document.head.appendChild(metaKeywords);

    // Schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Cómo Prevenir que la Batería se Descargue",
      "description": "Consejos profesionales para el mantenimiento preventivo de la batería del coche",
      "image": "https://images.unsplash.com/photo-1486302433359-d8f7d5d12b10?w=600&h=400&fit=crop",
      "author": {
        "@type": "Organization",
        "name": "ArrancaYa BCN"
      },
      "publisher": {
        "@type": "Organization",
        "name": "ArrancaYa BCN",
        "logo": "https://arrancaya-bcn.es/images/logo.png"
      },
      "datePublished": "2026-01-12",
      "dateModified": "2026-01-12"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Prevenir batería descargada',
      'page_location': window.location.href,
      'content_group1': 'Blog',
      'content_group2': 'Prevención'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) {
        document.head.removeChild(metaKeywords);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">Prevención</span>
            <span>•</span>
            <span>12 de enero, 2026</span>
            <span>•</span>
            <span>6 min de lectura</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            Cómo <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Prevenir</span> que la Batería se Descargue
          </h1>
          
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 border-2 border-green-100 mb-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              <strong className="text-green-600">Prevenir es mejor que curar</strong>. Aprende los secretos del mantenimiento 
              profesional para evitar quedarte con la batería descargada. Consejos de ArrancaYa BCN.
            </p>
          </div>
          
          {/* Dato impactante en lugar de imagen */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-6">
              <div className="text-6xl font-black text-green-500 mb-2">90%</div>
              <p className="text-xl text-gray-700 font-medium">
                de las averías de batería se pueden <strong>prevenir</strong> con cuidados básicos
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-blue-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-blue-600">300€</div>
                <p className="text-sm text-gray-600">Ahorras en servicios</p>
              </div>
              <div className="bg-green-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-green-600">5-7 años</div>
                <p className="text-sm text-gray-600">Vida útil con cuidados</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-purple-600">2-3 años</div>
                <p className="text-sm text-gray-600">Sin mantenimiento</p>
              </div>
            </div>
          </div>
        </header>

        <div className="prose prose-xl max-w-none space-y-12">
          
          {/* Mensaje principal */}
          <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-8">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-green-800 font-semibold mb-2">💡 El Secreto del Mantenimiento</p>
                <p className="text-green-700 mb-0">
                  El 90% de las averías de batería se pueden prevenir con cuidados básicos. 
                  Estas prácticas te ahorrarán llamadas de emergencia y dinero.
                </p>
              </div>
            </div>
          </div>

          <h2>1. Uso Regular del Vehículo</h2>
          
          <p><strong>La regla de oro: usa tu coche regularmente</strong></p>
          
          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-green-800 font-bold mb-3">✅ Uso Ideal:</h3>
              <ul className="text-green-700 space-y-2">
                <li>• <strong>Conducir mínimo 2-3 veces por semana</strong></li>
                <li>• Trayectos de al menos <strong>15-20 minutos</strong></li>
                <li>• Velocidad sostenida (no solo ciudad)</li>
                <li>• Permitir que el motor alcance temperatura</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-red-800 font-bold mb-3">❌ Evitar:</h3>
              <ul className="text-red-700 space-y-2">
                <li>• Dejar el coche parado <strong>más de 1 semana</strong></li>
                <li>• Solo trayectos muy cortos (menos de 5 min)</li>
                <li>• Arrancar solo para mover el coche</li>
                <li>• Uso solo en atascos urbanos</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 my-6">
            <p className="text-blue-800 mb-0">
              <strong>¿Por qué es tan importante?</strong> El alternador necesita que el motor funcione 
              a cierto régimen para cargar efectivamente la batería. Los trayectos muy cortos o 
              el coche parado consumen más energía de la que generan.
            </p>
          </div>

          <h2>2. Control de Consumos Eléctricos</h2>
          
          <h3>Antes de Salir del Coche:</h3>
          <ul>
            <li>✅ <strong>Apaga todas las luces</strong> (interiores, maletero, guantera)</li>
            <li>✅ <strong>Cierra completamente las puertas</strong> (luces de cortesía)</li>
            <li>✅ <strong>Desconecta cargadores</strong> y dispositivos USB</li>
            <li>✅ <strong>Apaga la radio</strong> completamente (no solo modo stand-by)</li>
            <li>✅ <strong>Verifica que las luces de posición</strong> estén apagadas</li>
          </ul>

          <h3>Consumos Ocultos Comunes:</h3>
          <ul>
            <li>🔍 <strong>Luz del maletero</strong> que no se apaga</li>
            <li>🔍 <strong>Luz de la guantera</strong></li>
            <li>🔍 <strong>Cargadores de móvil</strong> enchufados</li>
            <li>🔍 <strong>Sistemas de navegación</strong> en stand-by</li>
            <li>🔍 <strong>Alarmas aftermarket</strong> mal instaladas</li>
          </ul>

          <h2>3. Mantenimiento de los Terminales</h2>
          
          <p><strong>Los terminales sucios pueden descargar la batería</strong></p>

          <h3>Limpieza Mensual:</h3>
          <ol>
            <li><strong>Desconecta la batería</strong> (primero el negativo, luego positivo)</li>
            <li><strong>Limpia con bicarbonato</strong> y agua los terminales</li>
            <li><strong>Seca completamente</strong> antes de reconectar</li>
            <li><strong>Aplica vaselina neutra</strong> para prevenir corrosión</li>
            <li><strong>Reconecta</strong> (primero positivo, luego negativo)</li>
          </ol>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 my-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 font-semibold mb-2">⚠️ Señales de Corrosión</p>
                <p className="text-yellow-700 mb-0">
                  Polvo blanco-verdoso, terminales flojos o dificultad para arrancar. 
                  La corrosión puede causar hasta un 30% de pérdida de eficiencia.
                </p>
              </div>
            </div>
          </div>

          <h2>4. Cuidados Según la Temperatura</h2>
          
          <h3>En Invierno (Barcelona también tiene frío):</h3>
          <ul>
            <li>🌡️ La capacidad de la batería <strong>baja un 20% con frío</strong></li>
            <li>🌡️ Aparca en <strong>garaje cerrado</strong> si es posible</li>
            <li>🌡️ <strong>Calienta el motor</strong> antes de usar aire acondicionado</li>
            <li>🌡️ <strong>Evita trayectos muy cortos</strong> en días fríos</li>
            <li>🌡️ Considera un <strong>cargador de mantenimiento</strong> si el coche para mucho</li>
          </ul>

          <h3>En Verano:</h3>
          <ul>
            <li>☀️ El calor extremo también <strong>daña las baterías</strong></li>
            <li>☀️ Aparca en <strong>sombra</strong> cuando sea posible</li>
            <li>☀️ Comprueba el <strong>nivel de líquido</strong> (baterías con mantenimiento)</li>
            <li>☀️ El aire acondicionado <strong>exige más</strong> del sistema eléctrico</li>
          </ul>

          <h2>5. Revisiones Preventivas</h2>
          
          <h3>Cada 3 Meses:</h3>
          <ul>
            <li>🔍 <strong>Voltaje en reposo:</strong> debe ser 12.6V o más</li>
            <li>🔍 <strong>Voltaje con motor:</strong> debe ser 13.5-14.5V</li>
            <li>🔍 <strong>Limpieza de terminales</strong></li>
            <li>🔍 <strong>Comprobación visual</strong> (grietas, hinchazón)</li>
          </ul>

          <h3>Cada 6 Meses:</h3>
          <ul>
            <li>🔧 <strong>Test de carga</strong> en taller especializado</li>
            <li>🔧 <strong>Comprobación del alternador</strong></li>
            <li>🔧 <strong>Revisión del sistema de arranque</strong></li>
          </ul>

          <h2>6. Cuándo Cambiar la Batería</h2>
          
          <p><strong>Señales de que necesitas cambio:</strong></p>
          
          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-red-800 font-bold mb-3">🚨 Cambio Urgente:</h3>
              <ul className="text-red-700 space-y-1">
                <li>• <strong>Más de 5 años</strong> de antigüedad</li>
                <li>• Se descarga en <strong>menos de 1 semana</strong> parado</li>
                <li>• Arranque <strong>lento o dificultoso</strong></li>
                <li>• <strong>Luces tenues</strong> al ralentí</li>
                <li>• <strong>Grietas o hinchazón</strong> visible</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-yellow-800 font-bold mb-3">⚠️ Vigilar de Cerca:</h3>
              <ul className="text-yellow-700 space-y-1">
                <li>• <strong>Entre 3-4 años</strong> de uso</li>
                <li>• Voltaje en reposo <strong>menor a 12.4V</strong></li>
                <li>• <strong>Corrosión frecuente</strong> en terminales</li>
                <li>• Necesita <strong>"ayuda" ocasional</strong></li>
              </ul>
            </div>
          </div>

          <h2>7. Consejos Específicos para Barcelona</h2>
          
          <ul>
            <li>🏙️ <strong>Tráfico urbano:</strong> Los atascos constantes no permiten carga eficiente</li>
            <li>🏙️ <strong>Aparcamiento en calle:</strong> Variaciones de temperatura afectan más</li>
            <li>🏙️ <strong>Coches de fin de semana:</strong> Considera un cargador de mantenimiento</li>
            <li>🏙️ <strong>Humedad del mar:</strong> Mayor corrosión en zonas costeras</li>
            <li>🏙️ <strong>Contaminación:</strong> Puede acelerar el deterioro de componentes</li>
          </ul>

          <h2>8. Productos Útiles para el Mantenimiento</h2>
          
          <h3>Básicos (menos de 30€):</h3>
          <ul>
            <li>🛠️ <strong>Multímetro básico</strong> para medir voltaje</li>
            <li>🛠️ <strong>Cepillo de terminales</strong> metálico</li>
            <li>🛠️ <strong>Spray protector</strong> anti-corrosión</li>
            <li>🛠️ <strong>Vaselina neutra</strong></li>
          </ul>

          <h3>Avanzados (50-150€):</h3>
          <ul>
            <li>🔋 <strong>Cargador inteligente</strong> para mantenimiento</li>
            <li>🔋 <strong>Tester de batería</strong> digital</li>
            <li>🔋 <strong>Arrancador portátil</strong> (power bank)</li>
          </ul>

          <div className="bg-green-50 border-l-4 border-green-400 p-6 my-8">
            <div className="flex items-start">
              <Battery className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-green-800 font-semibold mb-2">💰 Ahorro a Largo Plazo</p>
                <p className="text-green-700 mb-0">
                  Una batería bien cuidada dura 5-7 años en lugar de 2-3. 
                  El mantenimiento preventivo te ahorra hasta 300€ en cambios prematuros y servicios de emergencia.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg p-8 mt-12">
          <div className="text-center">
            <Battery className="h-12 w-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">¿Tu Batería Ya Da Síntomas?</h2>
            <p className="text-lg mb-6 opacity-90">
              No esperes a quedarte tirado. En ArrancaYa BCN ofrecemos <strong>revisión completa</strong> 
              del sistema eléctrico y cambio de batería con las mejores marcas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-3 font-bold"
              >
                <Battery className="h-5 w-5 mr-2" />
                Revisión + Cambio
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="bg-white text-purple-600 border-2 border-white hover:bg-purple-50 px-8 py-3"
              >
                <Phone className="h-5 w-5 mr-2" />
                Consultar: 613 076 099
              </Button>
            </div>
            <p className="text-sm mt-4 opacity-80">
              🔋 Baterías de marca • 🔧 Instalación profesional • 📋 Diagnóstico completo
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default PrevenirBateriaDescargada;